<template>
  <div
    class="magazine-registration"
    :class="{
      'magazine-registration--open':
        registrationForm.visible || socialMagazineVisible,
    }"
  >
    <div class="magazine-registration__link-list">
      <div class="magazine-registration__link" @click="showSocial">
        <div>
          <dotcom-icon name="share-nodes" art="light" />
        </div>
        <div>{{ $t('socialLabel') }}</div>
      </div>
      <div class="magazine-registration__link" @click="showRegistrationForm">
        <div>
          <dotcom-icon name="envelope" art="light" />
        </div>
        <div>{{ $t('registrationLabel') }}</div>
      </div>
    </div>
    <div
      v-if="registrationForm.visible || socialMagazineVisible"
      class="magazine-registration__overlay"
    >
      <div v-if="registrationForm.visible" class="magazine-registration__form">
        <form
          v-if="
            registrationForm.state === 'form' ||
            registrationForm.state === 'waiting'
          "
          class="sanitasForms"
          @submit.prevent="registrationSubmit"
        >
          <h3>{{ $t('registrationFormTitle') }}</h3>
          <drop-down
            v-model="registrationForm.salutation"
            required
            :title-label="$t('registrationFormSalutationLabel')"
            :options="salutations"
            :errors="salutationErrors"
          />
          <text-input
            v-model="registrationForm.lastname"
            required
            :title-label="$t('registrationFormLastnameLabel')"
            :errors="lastnameErrors"
          />
          <text-input
            v-model="registrationForm.firstname"
            required
            :title-label="$t('registrationFormFirstnameLabel')"
            :errors="firstnameErrors"
          />
          <text-input
            v-model="registrationForm.email"
            required
            :title-label="$t('registrationFormEmailLabel')"
            :errors="emailErrors"
          />
          <sanitas-button
            class="registration-submit-button"
            primary
            type="submit"
            :is-spinner-active="registrationForm.state === 'waiting'"
          >
            {{ $t('registrationFormSubmitLabel') }}
          </sanitas-button>
          <div>
            {{ $t('registrationFormMandatoryLabel', {}, lang, false) }}
          </div>
        </form>
        <div
          v-if="registrationForm.state === 'success'"
          class="magazine-registration__message"
        >
          <h3>{{ $t('registrationFormSuccessTitle') }}</h3>
          <div>{{ $t('registrationFormSuccessMessage') }}</div>
        </div>
        <div
          v-if="registrationForm.state === 'error'"
          class="magazine-registration__message"
        >
          {{ $t('registrationFormSubmitError') }}
        </div>
      </div>
      <magazine-social-sharing
        v-if="socialMagazineVisible"
        class="magazine-registration__social"
        :social-magazine-visible="socialMagazineVisible"
        :links="socialLinks"
        @hideSocial="hideSocial"
      >
        <template slot="title">
          {{ $t('socialTitle') }}
        </template>
      </magazine-social-sharing>
      <a
        href
        class="magazine-registration__close"
        @click.prevent="hideRegistrationForm"
      >
        <dotcom-icon name="xmark" art="light" color="--c-secondary-neutral-2" />
      </a>
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'
import axios from 'axios'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

import { setTrackerVersion, trackForm } from '@/helpers/tracker'
import { email } from '@/helpers/validation'
import MagazineSocialSharing from '@/modules/magazine/article/MagazineSocialSharing'

export default {
  name: 'MagazineRegistration',

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    DropDown: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/DropDown'
      )
    },
    TextInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/TextInput'
      )
    },
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
    MagazineSocialSharing,
  },

  mixins: [validationMixin],

  props: {
    registrationFormSubmitUrl: {
      type: String,
      default: '',
    },
    registrationFormFormid: {
      type: String,
      default: '',
    },
    registrationFormFormstart: {
      type: String,
      default: '',
    },
    socialLinks: {
      type: String,
      default: '',
    },
  },

  setup() {
    const language = window.Sanitas.language || 'de'
    const loadLabels = lang => import(`../i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      lang: language,
      $t: $label,
    }
  },

  data() {
    return {
      registrationForm: {
        salutation: null,
        lastname: '',
        firstname: '',
        email: '',
        state: 'form',
        visible: false,
      },
      socialMagazineVisible: false,
    }
  },

  computed: {
    salutations() {
      return [
        {
          name: this.$t('registrationFormSalutationFLabel'),
          value: 2,
        },
        {
          name: this.$t('registrationFormSalutationMLabel'),
          value: 1,
        },
      ]
    },
    salutationErrors() {
      return this.$v.registrationForm.salutation.$invalid &&
        this.$v.registrationForm.salutation.$dirty
        ? { errors: this.$t('registrationFormSalutationError') }
        : null
    },
    lastnameErrors() {
      return this.$v.registrationForm.lastname.$invalid &&
        this.$v.registrationForm.lastname.$dirty
        ? { errors: this.$t('registrationFormLastnameError') }
        : null
    },
    firstnameErrors() {
      return this.$v.registrationForm.firstname.$invalid &&
        this.$v.registrationForm.firstname.$dirty
        ? { errors: this.$t('registrationFormFirstnameError') }
        : null
    },
    emailErrors() {
      return this.$v.registrationForm.email.$invalid &&
        this.$v.registrationForm.email.$dirty
        ? { errors: this.$t('registrationFormEmailError') }
        : null
    },
  },

  beforeMount() {
    setTrackerVersion()
  },

  validations: {
    registrationForm: {
      salutation: {
        required,
      },
      lastname: {
        required,
      },
      firstname: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },

  methods: {
    showRegistrationForm() {
      this.registrationForm.visible = true
      trackForm('Main18SanitasNewsletterMagazin', 'ComFormStarted')
      this.$emit('overlayOpened', true)
    },
    showSocial() {
      this.socialMagazineVisible = true
      this.$emit('overlayOpened', true)
    },
    hideSocial() {
      this.socialMagazineVisible = false
      this.$emit('overlayOpened', false)
    },
    hideRegistrationForm() {
      this.socialMagazineVisible = false
      this.registrationForm.visible = false
      if (this.registrationForm.state === 'error') {
        this.registrationForm.state = 'form'
      }
      this.$emit('overlayOpened', false)
    },
    registrationSubmit() {
      this.$v.$reset()
      this.$v.$touch()
      if (!this.$v.$invalid) {
        let registrationForm = this.registrationForm
        let data = new FormData()
        data.append('firstname', registrationForm.firstname)
        data.append('lastname', registrationForm.lastname)
        data.append('email', registrationForm.email)
        data.append('language', this.$t('registrationFormLanguageCode'))
        data.append('salutation', registrationForm.salutation)
        data.append(':formid', this.registrationFormFormid)
        data.append(':formstart', this.registrationFormFormstart)
        data.append('_charset_', 'UTF-8')
        data.append(':redirect', '/')

        registrationForm.state = 'waiting'
        trackForm('newsletter', 'ComFormSubmitted')

        axios({
          method: 'post',
          url: this.registrationFormSubmitUrl,
          data: data,
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
          .then(function (response) {
            registrationForm.state = 'success'
            checkRegistrationMessageScroll()
          })
          .catch(function (response) {
            registrationForm.state = 'error'
            checkRegistrationMessageScroll()
          })
      }

      function checkRegistrationMessageScroll() {
        // Scroll message into viewport on mobiles
        if (
          Math.max(
            document.documentElement.clientWidth,
            window.innerWidth || 0,
          ) < 480
        ) {
          if (window.scrollY > 280) {
            window.scrollBy(0, 260 - window.scrollY)
          }
        }
      }
    },
  },
}
</script>

<style>
.magazine-registration {
  padding: 0;
}

.magazine-registration--open {
  border-bottom: 1px solid var(--c-primary-neutral-1);
  margin-bottom: 0;
  margin-top: 0 !important;
}

.magazine-registration__overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  border-bottom: 1px solid var(--c-primary-neutral-1);
}

.magazine-registration__form,
.magazine-registration__social {
  padding: var(--body-offset-horizontal);
}

.magazine-registration__social {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .sanitas-magazine-social-divider,
  .sanitas-magazine-social-container {
    margin-left: 0 !important;
  }

  .sanitas-magazine-social.overlay {
    border-bottom: 0;
  }
}

.magazine-registration__link,
.magazine-registration__close {
  & >>> .dotcom-icon {
    font-size: 18px;
  }
}

.magazine-registration__close {
  position: absolute;
  top: calc(var(--body-offset-horizontal) - 8px);
  right: calc(var(--body-offset-horizontal) - 8px);
  padding: 8px;
  z-index: 99;

  &:hover {
    text-decoration: none;
    border-bottom: none;
  }
}

.magazine-registration__link-list {
  display: flex;
  justify-content: space-between;
  padding: var(--body-offset-horizontal) 0;
}

.magazine-registration__form {
  background-color: white;
  position: relative;
  z-index: 10;

  h3 {
    font-size: 20px;
    margin-bottom: 30px;
    font-weight: bold;
    color: var(--c-primary-neutral-1);
  }

  form {
    margin-left: 0;
    margin-right: auto;

    button.sanitas-button {
      margin-top: 30px;
      margin-bottom: 30px;
    }

    & > div {
      margin-bottom: 0 !important;
    }
  }
}

.magazine-registration__message {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  padding-bottom: 30px;
}

.magazine-registration__link {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--c-primary-neutral-1);
  font-weight: 400;
  font-style: normal;
  line-height: 1.1;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    border-bottom: none;
    color: var(--c-primary-color-3);
  }

  div + div {
    margin-left: 5px;
    white-space: nowrap;
  }

  & + .magazine-registration__link {
    margin-left: 20px;
  }
}

@media (--xs) {
  .magazine-registration__link {
    font-size: 16px;
    min-height: 30px;
  }

  .magazine-registration__link-list {
    justify-content: flex-start;
  }
}

@media (--md) {
  .magazine-registration__link-list {
    justify-content: flex-end;
  }
}
</style>
